import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useChargeQueries from '@dashboard/hooks/queries/useChargeQueries';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import { format } from 'date-fns';
import StatisticsList from './StatisticsList';
import Spinner from '@dashboard/components/Spinner/Spinner';
import { recentPaymentsKey } from '@dashboard/lib/queryKeys';

function useRecentPayments() {
  const { sellerId } = useAuth();

  const { getCharges } = useChargeQueries();

  const { data, isError, isLoading } = useQuery({
    queryKey: recentPaymentsKey(sellerId),
    queryFn: () =>
      getCharges(sellerId, {
        limit: 4,
        livemode: true,
        status: 'paid',
      }),
    staleTime: 1000 * 60, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  return {
    isLoading,
    isError,
    data,
  };
}

export default function RecentPayments() {
  const { data, isLoading, isError } = useRecentPayments();

  return (
    <StatisticsList.List>
      {isLoading && (
        <div>
          <Spinner />
        </div>
      )}

      {isError && <StatisticsList.EmptyState>Something went wrong</StatisticsList.EmptyState>}

      {!data?.length && <StatisticsList.EmptyState>No payments yet</StatisticsList.EmptyState>}

      {data?.map(payment => {
        const isBooking = payment.tickets && payment.tickets.length > 0;

        return (
          <StatisticsList.Item
            key={payment._id}
            href={isBooking ? `/orders/bookings/${payment._id}` : `/orders/payments/${payment._id}`}
          >
            <StatisticsList.Left>
              <StatisticsList.PrimaryText>{payment.customer.email}</StatisticsList.PrimaryText>
              <StatisticsList.SecondaryText>{payment.product.title}</StatisticsList.SecondaryText>
            </StatisticsList.Left>

            <StatisticsList.Right>
              <StatisticsList.PrimaryText>
                {getPriceLabel({
                  amount: payment.amount,
                  currency: payment.currency,
                })}
              </StatisticsList.PrimaryText>

              <StatisticsList.SecondaryText>
                {format(new Date(payment.createdAt), 'MMM d, yyyy HH:mm')}
              </StatisticsList.SecondaryText>
            </StatisticsList.Right>
          </StatisticsList.Item>
        );
      })}
    </StatisticsList.List>
  );
}
